import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Tag, ArrowUpRight, Sparkles, Calendar, Gift, Ticket, PartyPopper,
  Loader2, ExternalLink, Filter, Search,
} from "lucide-react";

/**
 * DealsFeed — protected member-facing page listing every deal that admins have
 * broadcasted (via the CPG Deals Console) and is still in its active window.
 * Backend: GET /api/deals/active — returns deals where sent_at is set and
 * starts_at ≤ now ≤ ends_at, respecting per-user package targeting.
 */

const KIND_META = {
  deal: { label: "Deal", icon: Tag, tone: "bg-primary/10 text-primary" },
  discount: { label: "Discount", icon: Ticket, tone: "bg-emerald-500/10 text-emerald-700" },
  perk: { label: "Perk", icon: Gift, tone: "bg-pink-500/10 text-pink-700" },
  event: { label: "Event", icon: PartyPopper, tone: "bg-sky-500/10 text-sky-700" },
};

const KIND_FILTERS = [
  { id: "all", label: "All" },
  { id: "deal", label: "Deals" },
  { id: "discount", label: "Discounts" },
  { id: "perk", label: "Perks" },
  { id: "event", label: "Events" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function DealCard({ deal }) {
  const meta = KIND_META[deal.kind] || KIND_META.deal;
  const Icon = meta.icon;
  const ends = fmtDate(deal.ends_at);
  const starts = fmtDate(deal.starts_at);
  const window = [starts, ends].filter(Boolean).join(" – ");

  return (
    <article
      data-testid={`deals-card-${deal.id}`}
      className="group relative rounded-2xl border border-shell-border bg-white overflow-hidden flex flex-col hover:shadow-md hover:-translate-y-0.5 transition"
    >
      {deal.image_url ? (
        <div className="aspect-[16/9] w-full overflow-hidden bg-shell-border/40 relative">
          <img
            src={deal.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition"
          />
          <span className={`absolute top-3 left-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${meta.tone} bg-white/95 backdrop-blur`}>
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
        </div>
      ) : (
        <div className="aspect-[16/9] w-full relative bg-gradient-to-br from-shell-side to-shell-side/90 grid place-items-center text-white/80">
          <Icon className="w-10 h-10" />
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] bg-white/95 text-shell-side">
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
        </div>
      )}

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-lg leading-snug tracking-tight" data-testid={`deals-title-${deal.id}`}>
          {deal.title}
        </h3>
        {deal.body && (
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed whitespace-pre-line line-clamp-5" data-testid={`deals-body-${deal.id}`}>
            {deal.body}
          </p>
        )}

        <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
          {window && (
            <span className="inline-flex items-center gap-1 rounded-full bg-shell-border/40 px-2.5 py-1">
              <Calendar className="w-3 h-3" /> {window}
            </span>
          )}
          {deal.sent_at && (
            <span className="inline-flex items-center gap-1 rounded-full bg-shell-border/40 px-2.5 py-1">
              Broadcast {fmtDate(deal.sent_at)}
            </span>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-shell-border">
          {deal.cta_url ? (
            <a
              href={deal.cta_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`deals-cta-${deal.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-4 py-2 text-xs font-semibold hover:bg-shell-side/90 transition"
            >
              {deal.cta_label || "Grab this deal"} <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span className="text-[11px] text-muted-foreground italic">Details inside — check your notifications.</span>
          )}
        </div>
      </div>
    </article>
  );
}

export default function DealsFeed() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/deals/active")
      .then((r) => { if (alive) setDeals(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (alive) toast.error(formatError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return deals.filter((d) => {
      if (kind !== "all" && d.kind !== kind) return false;
      if (!s) return true;
      return [d.title, d.body, d.cta_label].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    });
  }, [deals, kind, q]);

  const counts = useMemo(() => {
    const map = { all: deals.length };
    for (const d of deals) map[d.kind] = (map[d.kind] || 0) + 1;
    return map;
  }, [deals]);

  return (
    <AppShell title="CPG Deals">
      <div className="space-y-6" data-testid="deals-page">
        <PageHero
          eyebrow="Members-only"
          icon={Sparkles}
          title="CPG Deals & Perks"
          subtitle="Curated discounts, vendor perks, and event invites broadcast to Institute members. Deals stay here for the length of their active window — bookmark anything you don't want to miss."
        />

        {/* Filter row */}
        <div className="rounded-2xl border border-shell-border bg-white p-4 flex items-center gap-3 flex-wrap" data-testid="deals-filter-row">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-shell-border/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
            <Filter className="w-3 h-3" /> Filter
          </div>
          {KIND_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setKind(f.id)}
              data-testid={`deals-filter-${f.id}`}
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition border",
                kind === f.id
                  ? "bg-shell-side text-white border-shell-side"
                  : "bg-white text-foreground border-shell-border hover:border-primary/40",
              ].join(" ")}
            >
              {f.label}
              {counts[f.id] > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${kind === f.id ? "bg-white/20" : "bg-shell-border/50"}`}>
                  {counts[f.id]}
                </span>
              )}
            </button>
          ))}
          <div className="relative ml-auto flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search deals…"
              data-testid="deals-search-input"
              className="w-full rounded-full border border-shell-border bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-primary/40"
            />
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="deals-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading deals…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-shell-border bg-white p-10 text-center"
            data-testid="deals-empty"
          >
            <Tag className="w-10 h-10 mx-auto text-primary/40" />
            <p className="font-display text-xl mt-4">
              {deals.length === 0 ? "No active deals yet" : "No deals match this filter"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              {deals.length === 0
                ? "New CPG deals get broadcast weekly. Check back soon — or make sure the CPG Deals notification is on in your account settings."
                : "Try a different category or clear your search."}
            </p>
            {deals.length > 0 && (
              <button
                type="button"
                onClick={() => { setKind("all"); setQ(""); }}
                data-testid="deals-empty-reset"
                className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
              >
                Reset filters <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="deals-grid">
            {filtered.map((d) => (
              <DealCard key={d.id} deal={d} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
